// TypeScript file - properly typed
/**
 * Reusable Archive Patterns for RTK Query
 * 
 * This file contains common patterns and utilities for creating
 * consistent archive/restore operations across the API layer.
 */

import { EndpointBuilder } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { reduxLogger } from '@/core/utils/logger';
import { ApiResponse } from './crud';

/** Properly typed EndpointBuilder matching the project's baseQueryWithRetry signature. */
type AppEndpointBuilder = EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  string,
  string
>;

// ARCHIVE-SPECIFIC TYPES

export interface ArchiveRequest {
  id: string;
  reason?: string;
}

export interface RestoreRequest {
  id: string;
}

export interface BulkArchiveRequest {
  ids: string[];
  reason?: string;
}

export interface BulkRestoreRequest {
  ids: string[];
}

export interface ArchivedItem {
  id: string;
  archived_at?: string;
  archived_by?: string;
  archive_reason?: string;
}

export interface ArchiveParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  from_date?: string;
  to_date?: string;
  date_start?: string;
  date_end?: string;
  archived_by?: string;
  archivedBy?: string;
}

export interface ArchivedListResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// RESPONSE TRANSFORMERS

/**
 * Transform archived list response
 */
export const transformArchivedListResponse = <T>(
  response: ApiResponse<ArchivedListResponse<T>>
): ArchivedListResponse<T> => {
  return response.data || { data: [], total: 0, page: 1, limit: 20, totalPages: 0 };
};

// TAG GENERATORS

/**
 * Generate provide tags for archived list endpoints
 */
export const generateArchivedListProvideTags = <T extends ArchivedItem>(
  tagType: ApiTag,
  result?: T[]
) => {
  if (!result || result.length === 0) {
    return [{ type: tagType, id: 'ARCHIVED-LIST' }];
  }
  
  return [
    { type: tagType, id: 'ARCHIVED-LIST' },
    ...result.map(item => ({ type: tagType, id: item.id }))
  ];
};

/**
 * Generate invalidate tags for archive/restore mutations
 */
export const generateArchiveInvalidateTags = (
  activeTagType: ApiTag,
  archivedTagType: ApiTag,
  id?: string | string[]
) => {
  const tags = [
    { type: activeTagType, id: 'LIST' },
    { type: activeTagType, id: 'PAGINATED-LIST' },
    { type: activeTagType, id: 'INFINITE-LIST' },
    { type: archivedTagType, id: 'ARCHIVED-LIST' }
  ];

  if (id) {
    if (Array.isArray(id)) {
      tags.push(...id.map(i => ({ type: activeTagType, id: i })));
      tags.push(...id.map(i => ({ type: archivedTagType, id: i })));
    } else {
      tags.push({ type: activeTagType, id });
      tags.push({ type: archivedTagType, id });
    }
  }

  return tags;
};

// URL BUILDERS

/**
 * Build URL with archive-specific query parameters
 */
export const buildArchiveUrl = (baseUrl: string, params?: ArchiveParams): string => {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }

  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// OPTIMISTIC UPDATE HANDLERS

import { baseApi } from '../core/baseApi';
import { ApiTag } from './crud';
import type { AppDispatch } from '@/core/store';

/**
 * Handle optimistic updates for archive operations
 */
export const handleArchiveOptimisticUpdate = async (
  id: string,
  activeTagType: ApiTag,
  archivedTagType: ApiTag,
  dispatch: AppDispatch,
  queryFulfilled: Promise<unknown>
) => {
  // Immediate optimistic update - invalidate all list types for immediate UI removal
  dispatch(
    baseApi.util.invalidateTags([
      { type: activeTagType, id: 'LIST' },
      { type: activeTagType, id: 'PAGINATED-LIST' },
      { type: activeTagType, id: 'INFINITE-LIST' },
      { type: activeTagType, id }
    ])
  );

  try {
    await queryFulfilled;

    // Invalidate archived list to show the newly archived item
    dispatch(
      baseApi.util.invalidateTags([
        { type: archivedTagType, id: 'ARCHIVED-LIST' }
      ])
    );
  } catch (error) {
    reduxLogger.error('Archive operation failed:', error);
    // On error, re-fetch to ensure consistency
    dispatch(
      baseApi.util.invalidateTags([
        { type: activeTagType, id: 'LIST' },
        { type: activeTagType, id: 'PAGINATED-LIST' },
        { type: activeTagType, id: 'INFINITE-LIST' },
        { type: activeTagType, id }
      ])
    );
  }
};

/**
 * Handle optimistic updates for restore operations
 */
export const handleRestoreOptimisticUpdate = async (
  id: string,
  activeTagType: ApiTag,
  archivedTagType: ApiTag,
  dispatch: AppDispatch,
  queryFulfilled: Promise<unknown>
) => {
  // Immediate optimistic update
  dispatch(
    baseApi.util.invalidateTags([
      { type: archivedTagType, id: 'ARCHIVED-LIST' },
      { type: archivedTagType, id }
    ])
  );

  try {
    await queryFulfilled;

    // Invalidate all active list types to show the restored item
    dispatch(
      baseApi.util.invalidateTags([
        { type: activeTagType, id: 'LIST' },
        { type: activeTagType, id: 'PAGINATED-LIST' },
        { type: activeTagType, id: 'INFINITE-LIST' }
      ])
    );
  } catch (error) {
    reduxLogger.error('Restore operation failed:', error);
    // On error, re-fetch to ensure consistency
    dispatch(
      baseApi.util.invalidateTags([
        { type: archivedTagType, id: 'ARCHIVED-LIST' },
        { type: archivedTagType, id }
      ])
    );
  }
};

// ARCHIVE ENDPOINT CREATORS

/**
 * Create a query to get archived items
 */
export const createGetArchivedQuery = <T extends ArchivedItem>(
  baseUrl: string,
  archivedTagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.query<ArchivedListResponse<T>, ArchiveParams | void>({
  query: (params?: ArchiveParams) => {
    const url = buildArchiveUrl(baseUrl, params);
    return { url };
  },
  transformResponse: (response: ApiResponse<ArchivedListResponse<T> | { conversations: T[], total?: number, page?: number, limit?: number, totalPages?: number } | { contacts: T[], total?: number, page?: number, limit?: number, totalPages?: number }>) => {
    const responseData = response.data;
    // Handle both conversations and contacts response formats
    if ('conversations' in responseData && responseData.conversations) {
      return {
        data: responseData.conversations,
        total: responseData.total || 0,
        page: responseData.page || 0,
        limit: responseData.limit || 20,
        totalPages: responseData.totalPages || Math.ceil((responseData.total || 0) / (responseData.limit || 20))
      };
    } else if ('contacts' in responseData && responseData.contacts) {
      return {
        data: responseData.contacts,
        total: responseData.total || 0,
        page: responseData.page || 0,
        limit: responseData.limit || 20,
        totalPages: responseData.totalPages || Math.ceil((responseData.total || 0) / (responseData.limit || 20))
      };
    } else {
      // Fallback to original format if already in expected format
      return responseData as ArchivedListResponse<T>;
    }
  },
  providesTags: (result?: ArchivedListResponse<T>) => 
    generateArchivedListProvideTags(archivedTagType, result?.data)
});

/**
 * Create a query to get archived item details
 */
export const createGetArchivedDetailsQuery = <T extends ArchivedItem>(
  baseUrl: string,
  archivedTagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.query<T, string>({
  query: (id: string) => `${baseUrl}/archived/${id}`,
  transformResponse: (response: ApiResponse<T>) => response.data,
  providesTags: (result: T | undefined, error: unknown, id: string) => [
    { type: archivedTagType, id }
  ]
});

/**
 * Create an archive mutation (using toggle endpoint)
 */
export const createArchiveMutation = (
  baseUrl: string,
  activeTagType: ApiTag,
  archivedTagType: ApiTag,
  useToggleEndpoint = true
   
) => (builder: AppEndpointBuilder) => builder.mutation<void, ArchiveRequest>({
  query: ({ id, reason }: ArchiveRequest) => ({
    url: useToggleEndpoint 
      ? `${baseUrl}/${id}/toggle-archive`
      : `${baseUrl}/${id}/archive`,
    method: 'POST',
    body: useToggleEndpoint 
      ? { isArchived: true, reason }
      : { reason }
  }),
  async onQueryStarted({ id }: ArchiveRequest, { dispatch, queryFulfilled }: { dispatch: AppDispatch; queryFulfilled: Promise<unknown> }) {
    await handleArchiveOptimisticUpdate(
      id,
      activeTagType,
      archivedTagType,
      dispatch,
      queryFulfilled
    );
  }
});

/**
 * Create a restore mutation (using dedicated restore endpoint for archived items)
 */
export const createRestoreMutation = (
  baseUrl: string,
  activeTagType: ApiTag,
  archivedTagType: ApiTag,
  useToggleEndpoint = true
   
) => (builder: AppEndpointBuilder) => builder.mutation<unknown, RestoreRequest>({
  query: ({ id }: RestoreRequest) => {
    const url = useToggleEndpoint
      ? `${baseUrl}/archived/${id}/restore`
      : `${baseUrl}/${id}/restore`;
    reduxLogger.info('[createRestoreMutation] Generated URL:', { baseUrl, id, useToggleEndpoint, url });
    return {
      url,
      method: 'POST',
      body: {}
    };
  },
  transformResponse: (response: ApiResponse<unknown>) => response.data || response,
  async onQueryStarted({ id }: RestoreRequest, { dispatch, queryFulfilled }: { dispatch: AppDispatch; queryFulfilled: Promise<unknown> }) {
    await handleRestoreOptimisticUpdate(
      id,
      activeTagType,
      archivedTagType,
      dispatch,
      queryFulfilled
    );
  }
});

/**
 * Create a bulk archive mutation
 */
export const createBulkArchiveMutation = <T = void>(
  baseUrl: string,
  originalTagType: ApiTag,
  archivedTagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, BulkArchiveRequest>({
  query: ({ ids, reason }: BulkArchiveRequest) => ({
    url: `${baseUrl}/bulk-archive`,
    method: 'POST',
    body: { ids, reason }
  }),
  invalidatesTags: (result: T | undefined, error: unknown, { ids }: BulkArchiveRequest) => 
    generateArchiveInvalidateTags(originalTagType, archivedTagType, ids)
});

/**
 * Create a bulk restore mutation
 */
export const createBulkRestoreMutation = <T = void>(
  baseUrl: string,
  originalTagType: ApiTag,
  archivedTagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, BulkRestoreRequest>({
  query: ({ ids }: BulkRestoreRequest) => ({
    url: `${baseUrl}/archived/bulk-restore`,
    method: 'POST',
    body: { ids }
  }),
  invalidatesTags: (result: T | undefined, error: unknown, { ids }: BulkRestoreRequest) => 
    generateArchiveInvalidateTags(originalTagType, archivedTagType, ids)
});

/**
 * Create an unarchive mutation (alias for restore)
 */
export const createUnarchiveMutation = <T = void>(
  baseUrl: string,
  tagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, RestoreRequest>({
  query: ({ id }: RestoreRequest) => ({
    url: `${baseUrl}/${id}/unarchive`,
    method: 'POST'
  }),
  invalidatesTags: (result: T | undefined, error: unknown, { id }: RestoreRequest) => [
    { type: tagType, id: 'LIST' },
    { type: tagType, id: 'ARCHIVED-LIST' },
    { type: tagType, id }
  ]
});

/**
 * Create a permanent delete mutation
 */
export const createPermanentDeleteMutation = <T = void>(
  baseUrl: string,
  tagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, string>({
  query: (id: string) => {
    const url = `${baseUrl}/archived/${id}/permanent`;
    reduxLogger.info('[createPermanentDeleteMutation] Generated URL:', { baseUrl, id, url });
    return {
      url,
      method: 'DELETE'
    };
  },
  invalidatesTags: (result: T | undefined, error: unknown, id: string) => [
    { type: tagType, id: 'ARCHIVED-LIST' },
    { type: tagType, id }
  ]
});

/**
 * Create a bulk permanent delete mutation
 */
export const createBulkPermanentDeleteMutation = <T = void>(
  baseUrl: string,
  tagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, { ids: string[] }>({
  query: ({ ids }: { ids: string[] }) => ({
    url: `${baseUrl}/archived/bulk-delete`,
    method: 'POST',
    body: { ids }
  }),
  invalidatesTags: (result: T | undefined, error: unknown, { ids }: { ids: string[] }) => [
    { type: tagType, id: 'ARCHIVED-LIST' },
    ...ids.map(id => ({ type: tagType, id }))
  ]
});

// EXPORT ALL PATTERNS

export const archivePatterns = {
  // Query creators
  createGetArchivedQuery,
  createGetArchivedDetailsQuery,
  
  // Mutation creators
  createArchiveMutation,
  createRestoreMutation,
  createBulkArchiveMutation,
  createBulkRestoreMutation,
  createUnarchiveMutation,
  createPermanentDeleteMutation,
  createBulkPermanentDeleteMutation,
  
  // Utilities
  transformArchivedListResponse,
  generateArchivedListProvideTags,
  generateArchiveInvalidateTags,
  buildArchiveUrl,
  handleArchiveOptimisticUpdate,
  handleRestoreOptimisticUpdate
};